// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js/pure";
// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import StyledMemberPayments from "./styles";
import {
	useCancelSubscriptionMutation,
	useGetCurrentUserQuery,
	useGetDomainGymQuery,
	useStripeCheckSubscriptionStatusMutation,
} from "store/api/api";
import { useRecoilValue } from "recoil";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { userAtom } from "globalAtoms";
import { useSetRecoilState } from "recoil";
import { Button } from "components/Common/Button";
import { Typography, Flex, Divider, Modal, Button as AntBtn, notification } from "antd";
import {
	EditOutlined,
	RightOutlined,
	StopOutlined,
	WarningOutlined,
	PlusCircleOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useStripeCreateMemberCheckoutSessionMutation, useStripeCreateSetupSessionMutation } from "store/api/api";
import { LoadingOutlined } from "@ant-design/icons";
import { getUser } from "useInit";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
const MemberPayments = ({ setTitle }) => {
	const token = localStorage.getItem("creator_jwt_token");
	const split = token.split(".")[1];
	const formatted = JSON.parse(atob(split));
	const JWTID = formatted.id;
	let { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;
	const user = useRecoilValue(userAtom);
	const setUser = useSetRecoilState(userAtom);
	const userAssociations = user?.userAssociations;
	const associationInUse = userAssociations?.find((a) => a.gym_id === domainGymId) || {};
	const { subscription_id, subscription_status } = associationInUse;
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
	const { data: userData, refetch: refetchUserData } = useGetCurrentUserQuery({ user_id: JWTID });
	const [activeComponent, setActiveComponent] = useState("defaultPage");
	const [cancelSubscriptionTrigger, { isLoading: cancelLoading }] = useCancelSubscriptionMutation();
	const [checkSubscriptionStatusTrigger, subscriptionStatus] = useStripeCheckSubscriptionStatusMutation();
	const [createMemberSubscriptionSession] = useStripeCreateMemberCheckoutSessionMutation();
	const [createSetupSession] = useStripeCreateSetupSessionMutation();

	const amount = ((subscriptionStatus?.data?.amount ?? 999) / 100).toFixed(2);

	useEffect(() => {
		const checkSubscriptionStatus = async () => {
			if (subscription_id) {
				const checkSubscriptionStatusResponse = await checkSubscriptionStatusTrigger({
					subscription_id,
				});

				const subscriptionStatus = checkSubscriptionStatusResponse?.data?.status;

				if (subscriptionStatus === "canceled" && subscription_status === "ACTIVE") {
					await cancelSubscriptionTrigger({
						subscription_id: subscription_id,
						customer_id: userData?.customer_id,
						gym_id: domainGymId,
					}).unwrap();

					refetchUserData();
					window.location.reload();
				}
			}
		};

		checkSubscriptionStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription_id, checkSubscriptionStatusTrigger, domainGymId, subscription_status]);

	const confirmCancel = async () => {
		try {
			await cancelSubscriptionTrigger({
				subscription_id: subscription_id,
				customer_id: userData?.customer_id,
				gym_id: domainGymId,
			}).unwrap();

			refetchUserData();
			await getUser(setUser);
			updateActiveComponent("cancelledSubscriptionPage");
		} catch (error) {
			console.log("unable to cancel", error);
		}
		setConfirmCancelOpen(false);
	};
	const updateActiveComponent = (active) => {
		const activeComponentMap = {
			defaultPage: "Membership Info",
			cancelSubscriptionPage: "Cancel Subscription",
			cancelledSubscriptionPage: "Resubscribe",
			editPaymentPage: "Payment Method",
			memberSubscriptionPage: "Create Subscription",
		};
		setActiveComponent(active);
		setTitle?.(activeComponentMap[active]);
	};
	const subscriptionEndDate = subscriptionStatus?.data?.current_period_end
		? dayjs.unix(subscriptionStatus.data?.current_period_end).format("YYYY-MM-DD")
		: dayjs.unix(associationInUse?.subscription_start_date).add(1, "month").format("YYYY-MM-DD");

	const isActive = subscription_status === "ACTIVE" || subscription_status === "TRIAL";
	const fetchSetupClientSecret = useCallback(async () => {
		try {
			if (!user?.customer_id) {
				notification.error({ message: "Failed to create setup session. No customer ID found." });
				return;
			}

			const { data } = await createSetupSession({ customer_id: user?.customer_id, type: "user" });
			return data.client_secret;
		} catch (err) {
			notification.error({ message: "Failed to create setup session." });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSetupSession]);

	const fetchMemberSubscriptionClientSecret = useCallback(async () => {
		try {
			if (!domainGymId || !user?.customer_id) {
				notification.error({ message: "Failed to create checkout session. No customer ID found." });
				return;
			}

			const { data } = await createMemberSubscriptionSession({ gym_id: domainGymId, customer_id: user?.customer_id });
			return data.client_secret;
		} catch (err) {
			notification.error({ message: "Failed to create checkout session." });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createMemberSubscriptionSession, domainGymId]);

	const cancelSubscriptionPage = (
		<Flex className="cancel" gap={16} vertical>
			<Button onClick={() => updateActiveComponent("defaultPage")} className="primary-outlined" data-id="btnBackToMembership">
				Back
			</Button>
			<Button className="primary-filled" onClick={() => setConfirmCancelOpen(true)} data-id="btnConfirmCancelSubscription">
				Cancel Subscription
			</Button>
		</Flex>
	);

	const cancelledSubscriptionPage = (
		<Flex className="cancel" gap={16} vertical>
			<Flex className="alert" gap={16}>
				<WarningOutlined />
				<Typography.Text>
					Your gym membership will be cancelled from {subscriptionEndDate}. Please resubscribe to continue.
				</Typography.Text>
			</Flex>
			<Button onClick={() => updateActiveComponent("defaultPage")} className="primary-outlined">
				Back
			</Button>
			<Button
				onClick={() => updateActiveComponent("memberSubscriptionPage")}
				style={{ backgroundColor: "#27AE60" }}
				className="primary-filled"
			>
				Resubscribe
			</Button>
		</Flex>
	);

	const defaultPage = (
		<>
			{["TRIAL", "EXPIRED"].includes(subscription_status) && (
				<Flex
					gap={16}
					align="center"
					justify="space-between"
					onClick={() => updateActiveComponent("memberSubscriptionPage")}
					className="sb-member-payments-selector"
					data-id="createSubscriptionSection"
				>
					<PlusCircleOutlined style={{ fontSize: 20, fontWeight: 600 }} />
					<Typography.Text style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>Create Subscription</Typography.Text>
					<RightOutlined />
				</Flex>
			)}

			<Flex
				gap={16}
				align="center"
				justify="space-between"
				onClick={() => updateActiveComponent("editPaymentPage")}
				className="sb-member-payments-selector"
				data-id="editPaymentSection"
			>
				<EditOutlined style={{ fontSize: 20, fontWeight: 600 }} />
				<Typography.Text style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>Edit Payment Method</Typography.Text>
				<RightOutlined />
			</Flex>

			{["ACTIVE", "CANCELLED"].includes(subscription_status) && (
				<Flex
					gap={16}
					align="center"
					justify="space-between"
					className="sb-member-payments-selector"
					onClick={() =>
						updateActiveComponent(
							subscription_status === "CANCELLED" ? "cancelledSubscriptionPage" : "cancelSubscriptionPage"
						)
					}
				>
					{subscription_status === "CANCELLED" ? (
						<>
							<ReloadOutlined style={{ fontSize: 20, fontWeight: 600, color: "green" }} />
							<Typography.Text style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "green" }}>
								Resubscribe
							</Typography.Text>
							<RightOutlined />
						</>
					) : (
						<>
							<StopOutlined style={{ fontSize: 20, fontWeight: 600, color: "red" }} />
							<Typography.Text style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "red" }} data-id="lblCancelSubscription">
								Cancel Subscription
							</Typography.Text>
							<RightOutlined />
						</>
					)}
				</Flex>
			)}
		</>
	);

	const memberSubscriptionPage = (
		<div id="checkout" data-id="memberSubscriptionPage">
			<EmbeddedCheckoutProvider
				stripe={stripePromise}
				options={{
					fetchClientSecret: fetchMemberSubscriptionClientSecret,
					onComplete: async () => {
						updateActiveComponent("defaultPage");
						refetchUserData();
						await getUser(setUser);
					},
				}}
			>
				<EmbeddedCheckout />
			</EmbeddedCheckoutProvider>
			<AntBtn
				style={{ marginTop: 16, width: "100%" }}
				onClick={() => updateActiveComponent("defaultPage")}
				className="primary-color"
				color="primary"
				data-id="btnCancelCreateSubscription"
			>
				Cancel
			</AntBtn>
		</div>
	);

	const editPaymentPage = (
		<div id="checkout" data-id="editPaymentPage">
			<EmbeddedCheckoutProvider
				stripe={stripePromise}
				options={{
					fetchClientSecret: fetchSetupClientSecret,
					onComplete: async () => {
						updateActiveComponent("defaultPage");
						refetchUserData();
						await getUser(setUser);
					},
				}}
			>
				<EmbeddedCheckout />
			</EmbeddedCheckoutProvider>
			<AntBtn
				style={{ marginTop: 16, width: "100%" }}
				onClick={() => updateActiveComponent("defaultPage")}
				className="primary-color"
				color="primary"
				data-id="btnCancelPayment"
			>
				Cancel
			</AntBtn>
		</div>
	);

	const activeComponentMap = {
		defaultPage,
		cancelSubscriptionPage,
		cancelledSubscriptionPage,
		editPaymentPage,
		memberSubscriptionPage,
	};

	return (
		<StyledMemberPayments className="sb-member-payments">
			<Modal
				destroyOnClose
				width={540}
				footer={null}
				onClose={() => setConfirmCancelOpen(false)}
				onCancel={() => setConfirmCancelOpen(false)}
				open={confirmCancelOpen}
				maskClosable={false}
				data-id="cancelSubscriptionConfirmModal"
			>
				<Flex vertical align="center" justify="center">
					<WarningOutlined style={{ fontSize: 24, color: "#D44215" }} />
					<Typography.Title level={2}>Cancel Subscription</Typography.Title>
					<Typography.Paragraph strong style={{ maxWidth: 312, textAlign: "center" }}>
						Are you sure you want to cancel your subscription?
					</Typography.Paragraph>
				</Flex>
				<Flex justify="space-between" align="center" gap={16} className="cancel" style={{ marginTop: 100 }}>
					<AntBtn
						className="primary-color"
						color="primary"
						style={{ flex: 1 }}
						onClick={() => setConfirmCancelOpen(false)}
						loading={cancelLoading}
						data-id="btnCancel"
					>
						No
					</AntBtn>
					<AntBtn
						variant="solid"
						color="primary"
						style={{ flex: 1 }}
						onClick={confirmCancel}
						loading={cancelLoading}
						disabled={cancelLoading}
						data-id="btnConfirm"
					>
						Confirm
					</AntBtn>
				</Flex>
			</Modal>
			<Typography.Paragraph>{domainGymResponse?.data?.member_subscription_description}</Typography.Paragraph>
			<Flex justify="center" align="center" vertical className="sb-member-payments-banner" data-id="lblSubscriptionInfo">
				<Flex gap={8} align="center" justify="center" data-id="btnCancelPayment">
					<div className={`dot ${isActive ? "active" : ""}`} />
					<Typography.Text>{subscription_status}</Typography.Text>
				</Flex>
				{associationInUse?.subscription_id && (
					<>
						{subscriptionStatus.isLoading ? (
							<LoadingOutlined style={{ color: "#fff" }} spin size="small" />
						) : (
							<Typography.Title level={3}>{`$${amount} / Month`}</Typography.Title>
						)}
						<Typography.Text>
							{subscription_status === "CANCELLED" ? "Cancellation Date" : "Next Renewal Date"}: {subscriptionEndDate}
						</Typography.Text>
					</>
				)}
			</Flex>
			<Divider />
			{activeComponentMap[activeComponent]}
		</StyledMemberPayments>
	);
};

export default MemberPayments;

